import {
  DocumentCategoryDto,
  GetDocumentsByCategoryParams,
} from "apis/data-contracts";
import Board from "components/Common/Board/Board";
import FilterListItem from "components/Common/PageFilterPannel/FilterListItem";
import { PageFilterPanel } from "components/Common/PageFilterPannel";
import FilterSearch from "components/Common/PageFilterPannel/FilterSearch";
import SearchEmpty from "components/Common/Empty/SearchEmpty";
import DateRangePicker from "components/foundation/DateRangePicker";
import Pagination from "components/foundation/Pagination";
import Select from "components/foundation/Select/Select";
import Tab from "components/foundation/Tab";
import useMyDeskDocumentsQuery from "features/document/hooks/queries/useMyDeskDocumentsQuery";
import useMyDeskAutoCompleteQuery from "features/my-page/queries/useMyDeskAutoCompleteQuery";
import { DATE_FORMAT } from "config/constant";
import dayjs from "dayjs";
import {
  MyDeskSearchOptions,
  MyDeskSummaryStatus,
} from "models/document/MyDeskSearchOptions";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import removeEmptyFields from "utils/removeEmptyFields";
import MyDeskStatusSummary from "../MyDeskStatusSummary";
import {
  analysisDocumentStatusList,
  documentStatusList,
  normalizeMyDeskDocumentStatus,
  MyDeskStatusFilterValue,
} from "../myDeskStatus";
import DeskTable from "./DeskTable";

interface FormData {
  search: string;
  startDate?: string;
  endDate?: string;
}

interface DeskTableBoardProps {
  categories: DocumentCategoryDto[];
  searchState: MyDeskSearchOptions;
  setSearchOptions: React.Dispatch<React.SetStateAction<MyDeskSearchOptions>>;
}

const DeskTableWebPage = ({
  categories,
  searchState,
  setSearchOptions,
}: DeskTableBoardProps) => {
  const ANALYSIS_PENDING_SUMMARY_STATUS: MyDeskSummaryStatus =
    "ANALYSIS_PENDING";
  const methods = useForm<FormData>();

  const { selectedTab, page, ...searchOptions } = searchState;

  const { data } = useMyDeskDocumentsQuery(
    removeEmptyFields({
      categoryUuid: selectedTab.uuid,
      page: page - 1,
      size: 10,
      searchTerm: searchOptions.search,
      startDate:
        searchOptions.startDate &&
        dayjs(searchOptions.startDate).format("YYYY-MM-DD"),
      endDate:
        searchOptions.endDate &&
        dayjs(searchOptions.endDate).format("YYYY-MM-DD"),
      documentStatus: (searchOptions.summaryStatus ||
        searchOptions.documentStatus) as GetDocumentsByCategoryParams["documentStatus"],
    }),
  );
  const { data: autoCompleteData } = useMyDeskAutoCompleteQuery({
    searchTerm: methods.watch("search"),
  });

  const documents = data.data.result.contents;

  const handleSearch = (formData: FormData) => {
    setSearchOptions((prev) => ({ ...prev, ...formData }));
  };

  const handleSummaryStatusChange = (value: MyDeskStatusFilterValue) => {
    if (selectedTab.name === "ANALYSIS" && value === "APPROVE_IN_PROGRESS") {
      setSearchOptions((prev) => ({
        ...prev,
        summaryStatus: ANALYSIS_PENDING_SUMMARY_STATUS,
        documentStatus: undefined,
        page: 1,
      }));
      return;
    }

    setSearchOptions((prev) => ({
      ...prev,
      summaryStatus: undefined,
      documentStatus: normalizeMyDeskDocumentStatus(value),
      page: 1,
    }));
  };

  const handleDocumentStatusChange = (value: MyDeskStatusFilterValue) => {
    setSearchOptions((prev) => ({
      ...prev,
      summaryStatus: undefined,
      documentStatus: normalizeMyDeskDocumentStatus(value),
      page: 1,
    }));
  };

  const [isReset, setIsReset] = useState<boolean>();
  const handleSearchReset = () => {
    setSearchOptions((prev) => ({
      ...prev,
      search: "",
      startDate: "",
      endDate: "",
      page: 1,
    }));

    methods.setValue("search", "");

    setIsReset(!isReset);
  };

  return (
    <>
      <form onSubmit={methods.handleSubmit(handleSearch)}>
        <FormProvider {...methods}>
          <PageFilterPanel.Board title="MY DESK">
            <FilterLayout>
              <FilterListItem title="등록일">
                <DateRangePicker
                  value={{
                    start: searchOptions.startDate
                      ? dayjs(searchOptions.startDate)
                          .format(DATE_FORMAT)
                          .toString()
                      : "",
                    end: searchOptions.endDate
                      ? dayjs(searchOptions.endDate)
                          .format(DATE_FORMAT)
                          .toString()
                      : "",
                  }}
                  onChangeDate={(dateRange) => {
                    setSearchOptions((prev) => ({
                      ...prev,
                      startDate: dateRange.start.toString(),
                      endDate: dateRange.end?.toString() || "",
                    }));
                  }}
                  onClickReset={isReset}
                />
              </FilterListItem>
              <FilterSearch
                autocomplete={autoCompleteData?.data.result.map((item) => ({
                  label: item,
                  value: item,
                }))}
                onSearchReset={handleSearchReset}
              />
            </FilterLayout>
          </PageFilterPanel.Board>
        </FormProvider>
      </form>
      <DeskTableBoardLayout>
        <DeskTableTabMenu>
          <div className="desk_tab-list">
            {categories.map((category) => (
              <DeskCategoryTab
                key={category.uuid}
                selected={category.uuid === selectedTab.uuid}
                value={category.uuid}
                onClick={() => {
                  setSearchOptions((prev) => ({
                    ...prev,
                    selectedTab: category,
                    summaryStatus: undefined,
                    page: 1,
                  }));
                }}
              >
                {category.name}({category.totalCount})
              </DeskCategoryTab>
            ))}
          </div>
        </DeskTableTabMenu>

        <DeskTableFilterMenu>
          <MyDeskStatusSummary
            categoryUuid={selectedTab.uuid}
            selectedStatus={
              searchState.summaryStatus === ANALYSIS_PENDING_SUMMARY_STATUS
                ? "APPROVE_IN_PROGRESS"
                : searchState.documentStatus
            }
            onSelectStatus={handleSummaryStatusChange}
          />
          <Select
            placeholder="문서 상태"
            list={
              selectedTab.name === "ANALYSIS"
                ? analysisDocumentStatusList
                : documentStatusList
            }
            style={{ width: "19rem", height: "4.6rem" }}
            value={searchState.documentStatus || " "}
            onValueChange={(value) =>
              handleDocumentStatusChange(value as MyDeskStatusFilterValue)
            }
          />
        </DeskTableFilterMenu>

        {data.data.result.totalElements === 0 && <SearchEmpty />}
        {documents.length > 0 && (
          <>
            <DeskTable
              categoryId={selectedTab.uuid}
              data={documents}
              page={page}
              totalData={data.data.result.totalElements}
            />
            <div
              style={{
                display: "flex",
                marginTop: "2rem",
              }}
            >
              <Pagination
                currentPage={page}
                lastPage={data.data.result.totalPages}
                onClickPageButton={(nextPage) =>
                  setSearchOptions((prev) => ({ ...prev, page: nextPage }))
                }
                style={{
                  marginLeft: "auto",
                }}
              />
            </div>
          </>
        )}
      </DeskTableBoardLayout>
    </>
  );
};

export default DeskTableWebPage;

const DeskTableBoardLayout = styled(Board)`
  padding: 4rem;
  margin-top: 2rem;
  min-height: 56rem;
`;

const DeskTableTabMenu = styled.div`
  width: 100%;

  .desk_tab-list {
    display: flex;
    gap: 0;
    margin-bottom: 2.4rem;
    width: 100%;
  }
`;

const DeskCategoryTab = styled(Tab)`
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  height: 5.1rem;
  padding: 0 1.6rem;
  border-bottom: 0.2rem solid #edeef3;
  font-size: 1.5rem;
  line-height: 2.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.2s ease,
    border-bottom-color 0.2s ease;

  ${({ selected }) =>
    selected &&
    css`
      background-color: #eee1e5;
      border-bottom-color: #a99198;
      border-radius: 0.5rem 0.5rem 0 0;
    `}
`;

const DeskTableFilterMenu = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

const FilterLayout = styled.div`
  display: flex;
  gap: 6rem;
`;
